/* FOM · sessao no navegador (D-FOM-037).

   O que este modulo NAO faz, e nao pode fazer:
     · nao guarda senha;
     · nao carrega segredo de JWT;
     · nao autentica localmente — quem decide e o backend;
     · nao usa o navegador como fonte de verdade de dado;
     · **nao guarda o refresh token.**

   O ULTIMO ITEM E A MUDANCA DA FASE 1L
   ------------------------------------

   Ate aqui o refresh token vivia em `sessionStorage` — legivel por qualquer
   script da pagina. Agora ele vive num cookie `HttpOnly`, emitido pelo
   servidor: o JavaScript da propria aplicacao nao consegue le-lo, e por isso
   um XSS tambem nao consegue.

   Consequencia pratica: **nao ha mais nada para este modulo guardar.** Retomar
   sessao apos um F5 deixou de ser "ler o token que guardei" e passou a ser
   "pedir renovacao e deixar o navegador anexar o cookie". O modulo some como
   deposito e permanece apenas como tela de entrada.

   O access token continua so em memoria, e continua sendo o unico credencial
   que o codigo manipula — ele viaja no cabecalho `Authorization`, que o
   navegador nunca envia sozinho. E por isso que as rotas de dominio sao imunes
   a CSRF por construcao.

   A ENTRADA INSTITUCIONAL (FOM-ARQ-0076)
   --------------------------------------

   A tela deixou de ser um cartao solto no meio do vazio e passou a ser a PORTA
   do produto: de um lado a identidade (Globalatech, marca-mae, e o FOM, o
   produto); do outro a autenticacao. Nada abaixo mudou de contrato:

     · o formulario continua chamando `aoEntrar(email, senha)`;
     · `primeiroAcesso` continua aparecendo SO quando o servidor diz que ele
       existe, continua nascendo fechado e continua sem campo de papel ou de
       organizacao;
     · as classes que os testes guardam (`.login-primeiro`, `.primeiro-form`,
       `.primeiro-erro`, `.primeiro-ok`, `.login-erro`, `[name=…]`) continuam
       exatamente com os mesmos nomes.

   O que a tela ganhou: hierarquia (marca -> produto -> acesso), um caminho
   honesto para quem nao tem conta ("Solicitar acesso" — que EXPLICA, e nao
   inventa endpoint), mostrar/ocultar senha, estado de carregamento anunciado
   e uma composicao propria de celular em vez de um desktop encolhido.

   A marca e usada a partir dos arquivos OFICIAIS copiados sem alteracao para
   `public/marca/` (fonte: `SaaS/globalatech-brand/assets/logo/`). Nenhum
   desenho de marca foi refeito aqui: o unico grafico autoral e a malha
   abstrata de fundo, que e decorativa, nao carrega dado nenhum e esta marcada
   como `aria-hidden`. */

export type TelaLoginOpcoes = {
  aoEntrar: (email: string, senha: string) => Promise<void>;
  dica?: { email: string; nota: string } | null;
  /* Presente apenas enquanto a instalacao nao tem ninguem dentro. Quando e
     `null`, a tela nao menciona primeiro acesso — porque ele nao existe. */
  primeiroAcesso?: {
    aoCriar: (nome: string, email: string, senha: string, confirmacao: string) => Promise<void>;
    minimoSenha: number;
  } | null;
};

/* Malha abstrata: nos e conexoes, sem rotulo, sem numero, sem dado. E uma
   ALUSAO ao mapa operacional, nao uma amostra dele — inventar um mapa de
   mentira na porta do produto seria o mesmo erro que o FOM existe para
   combater. O unico ponto de ouro e o "no de oportunidade" da marca
   (MANUAL_DE_MARCA §2: o olho pode viajar sozinho como marcador de insight). */
const MALHA = `
<svg class="entrada-malha" viewBox="0 0 520 520" aria-hidden="true" focusable="false">
  <g class="malha-linhas" fill="none" stroke="currentColor" stroke-width="1">
    <path d="M78 118 L214 74 L358 132 L446 96"/>
    <path d="M78 118 L132 246 L214 74"/>
    <path d="M132 246 L268 224 L358 132"/>
    <path d="M268 224 L332 348 L446 96"/>
    <path d="M132 246 L96 392 L246 434"/>
    <path d="M332 348 L246 434 L268 224"/>
    <path d="M332 348 L452 300"/>
    <path d="M96 392 L48 268 L78 118"/>
  </g>
  <g class="malha-nos">
    <circle cx="78"  cy="118" r="4.5"/>
    <circle cx="214" cy="74"  r="3.5"/>
    <circle cx="358" cy="132" r="4"/>
    <circle cx="446" cy="96"  r="3"/>
    <circle cx="132" cy="246" r="5"/>
    <circle cx="48"  cy="268" r="3"/>
    <circle cx="96"  cy="392" r="3.5"/>
    <circle cx="246" cy="434" r="4"/>
    <circle cx="332" cy="348" r="4.5"/>
    <circle cx="452" cy="300" r="3"/>
  </g>
  <circle class="malha-no-ouro" cx="268" cy="224" r="6.5"/>
</svg>`;

/* Icones do campo de senha. Traco fino, mesma linguagem dos icones da
   navegacao (viewBox 24, stroke, sem preenchimento). */
const OLHO = `<svg viewBox="0 0 24 24" aria-hidden="true" focusable="false"><path d="M2 12s3.6-6 10-6 10 6 10 6-3.6 6-10 6-10-6-10-6Z"/><circle cx="12" cy="12" r="2.6"/></svg>`;
const OLHO_CORTADO = `<svg viewBox="0 0 24 24" aria-hidden="true" focusable="false"><path d="M2 12s3.6-6 10-6c1.7 0 3.2.4 4.5 1M22 12s-3.6 6-10 6c-1.7 0-3.2-.4-4.5-1"/><path d="M4 4l16 16"/></svg>`;

export function montarTelaLogin(host: HTMLElement, opcoes: TelaLoginOpcoes): void {
  host.innerHTML = '';
  host.className = 'tela-login';

  const palco = document.createElement('div');
  palco.className = 'entrada-palco';

  /* ------------------------------------------------------ lado institucional
     Ordem de leitura deliberada: MARCA-MAE (Globalatech) -> PRODUTO (FOM) ->
     o que ele e. Quem chega tem de saber de quem e o software antes de saber
     o que ele faz. */
  const institucional = document.createElement('aside');
  institucional.className = 'entrada-institucional';
  institucional.innerHTML = `
    ${MALHA}
    <div class="entrada-institucional-corpo">
      <img class="entrada-logo" src="/marca/globalatech-logo-primary.svg"
           alt="Globalatech" width="560" height="120" />
      <div class="entrada-produto">
        <p class="entrada-eyebrow">Produto operacional</p>
        <h1 class="entrada-sigla">FOM</h1>
        <p class="entrada-nome">Forwarder Operating Map</p>
        <p class="entrada-descricao">O mapa operacional vivo da organizacao.</p>
      </div>
      <p class="entrada-rodape">Acesso restrito a usuarios autorizados pela organizacao.</p>
    </div>`;

  /* ------------------------------------------------------------ lado acesso */
  const acesso = document.createElement('section');
  acesso.className = 'entrada-acesso';

  const cartao = document.createElement('form');
  cartao.className = 'login-cartao';
  cartao.noValidate = true;

  cartao.innerHTML = `
    <div class="login-cabecalho">
      <h2 class="login-titulo">Entrar</h2>
      <p class="login-sub">Use as credenciais fornecidas pela sua organizacao.</p>
    </div>
    <label class="campo" for="login-email">
      <span>E-mail</span>
      <input id="login-email" name="email" type="email" autocomplete="username"
             inputmode="email" spellcheck="false" autocapitalize="none"
             enterkeyhint="next" required aria-describedby="login-erro" />
    </label>
    <label class="campo campo-senha" for="login-senha">
      <span>Senha</span>
      <span class="campo-senha-caixa">
        <input id="login-senha" name="senha" type="password" autocomplete="current-password"
               enterkeyhint="go" required aria-describedby="login-erro" />
        <button type="button" class="campo-olho" data-ver-senha
                aria-pressed="false" aria-label="Mostrar senha" title="Mostrar senha">${OLHO}</button>
      </span>
    </label>
    <button class="botao-principal login-entrar" type="submit">
      <span class="login-entrar-rotulo">Entrar</span>
    </button>
    <p class="login-erro" id="login-erro" role="alert" hidden></p>
  `;

  const erro = cartao.querySelector<HTMLParagraphElement>('.login-erro')!;
  const botao = cartao.querySelector<HTMLButtonElement>('button[type=submit]')!;
  const rotulo = cartao.querySelector<HTMLSpanElement>('.login-entrar-rotulo')!;
  const email = cartao.querySelector<HTMLInputElement>('[name=email]')!;
  const senha = cartao.querySelector<HTMLInputElement>('[name=senha]')!;

  /* Mostrar/ocultar senha. O botao NAO e um input — o tipo do campo continua
     `password` no momento em que a tela nasce, que e o que a auditoria de
     bootstrap confere. */
  const verSenha = cartao.querySelector<HTMLButtonElement>('[data-ver-senha]')!;
  verSenha.addEventListener('click', () => {
    const revelada = senha.type === 'text';
    senha.type = revelada ? 'password' : 'text';
    verSenha.setAttribute('aria-pressed', String(!revelada));
    const texto = revelada ? 'Mostrar senha' : 'Ocultar senha';
    verSenha.setAttribute('aria-label', texto);
    verSenha.title = texto;
    verSenha.innerHTML = revelada ? OLHO : OLHO_CORTADO;
    senha.focus();
  });

  if (opcoes.dica) {
    email.value = opcoes.dica.email;
    const nota = document.createElement('p');
    nota.className = 'login-nota';
    nota.textContent = opcoes.dica.nota;
    cartao.appendChild(nota);
  }

  if (opcoes.primeiroAcesso) {
    montarPrimeiroAcesso(cartao, opcoes.primeiroAcesso);
  }

  cartao.addEventListener('submit', (ev) => {
    ev.preventDefault();
    erro.hidden = true;
    botao.disabled = true;
    botao.classList.add('carregando');
    botao.setAttribute('aria-busy', 'true');
    rotulo.textContent = 'Entrando…';
    void opcoes
      .aoEntrar(email.value.trim(), senha.value)
      .catch((e: Error) => {
        erro.textContent = e.message === 'invalid_credentials'
          ? 'Credencial invalida.'
          : e.message;
        erro.hidden = false;
      })
      .finally(() => {
        botao.disabled = false;
        botao.classList.remove('carregando');
        botao.removeAttribute('aria-busy');
        rotulo.textContent = 'Entrar';
      });
  });

  acesso.appendChild(cartao);
  acesso.appendChild(montarSolicitarAcesso());

  palco.appendChild(institucional);
  palco.appendChild(acesso);
  host.appendChild(palco);
  email.focus();
}


/* ====================================================== SOLICITAR ACESSO ===

   O FOM e multi-tenant e governado: conta nasce DENTRO de uma organizacao que
   ja existe, por decisao de quem a administra. Uma porta de "criar conta" aqui
   ou criaria organizacao sozinha (o que a governanca proibe) ou prometeria um
   endpoint que nao existe.

   Entao a tela nao promete: ela EXPLICA o caminho real. Nenhum formulario,
   nenhum envio, nenhum endereco inventado — quem precisa de acesso fala com
   quem administra o FOM na propria empresa. No dia em que existir um endpoint
   de solicitacao, este bloco e o lugar onde ele entra. */
function montarSolicitarAcesso(): HTMLElement {
  const bloco = document.createElement('div');
  bloco.className = 'entrada-solicitar';
  bloco.innerHTML = `
    <button type="button" class="entrada-link" data-solicitar
            aria-expanded="false" aria-controls="entrada-solicitar-nota">Solicitar acesso</button>
    <p class="entrada-solicitar-nota" id="entrada-solicitar-nota" hidden>
      O acesso ao FOM e concedido pela sua organizacao. Fale com quem administra
      o FOM na sua empresa para receber um convite — a conta e criada por dentro,
      com papel e permissoes definidos por quem responde pela organizacao.
    </p>`;

  const botao = bloco.querySelector<HTMLButtonElement>('[data-solicitar]')!;
  const nota = bloco.querySelector<HTMLParagraphElement>('.entrada-solicitar-nota')!;
  botao.addEventListener('click', () => {
    nota.hidden = !nota.hidden;
    botao.setAttribute('aria-expanded', String(!nota.hidden));
  });
  return bloco;
}


/* ======================================================= PRIMEIRO ACESSO ===

   Aparece SOMENTE quando o servidor diz que a instalacao ainda nao tem
   identidade humana. Nao e cadastro: e a unica vez, na vida da instalacao, em
   que alguem pode entrar sem que exista alguem para autorizar.

   A tela nao decide nada disso. Ela pergunta ao servidor se deve aparecer, e o
   servidor e quem sabe — inclusive porque a mesma resposta depende de uma
   variavel de ambiente que so quem opera o servidor controla.

   Papel e organizacao nao aparecem no formulario, e nao e por economia de
   espaco: nao ha campo porque nao ha escolha. O servidor recusa o pedido que
   tentar mandar qualquer um dos dois. */

type PrimeiroAcesso = NonNullable<TelaLoginOpcoes['primeiroAcesso']>;

function montarPrimeiroAcesso(cartao: HTMLElement, opcoes: PrimeiroAcesso): void {
  const bloco = document.createElement('div');
  bloco.className = 'login-primeiro';
  bloco.innerHTML = `
    <div class="login-separador"><span>ou</span></div>
    <button type="button" class="botao" data-abrir>Primeiro acesso</button>
    <div class="primeiro-form" hidden>
      <p class="primeiro-nota">
        Esta organizacao ainda nao tem nenhum usuario. Crie o primeiro acesso —
        ele sera o responsavel pela organizacao. Depois disso, esta opcao
        desaparece.
      </p>
      <label class="campo">
        <span>Nome</span>
        <input name="p-nome" type="text" autocomplete="name" />
      </label>
      <label class="campo">
        <span>E-mail</span>
        <input name="p-email" type="email" autocomplete="username" spellcheck="false" autocapitalize="none" />
      </label>
      <label class="campo">
        <span>Senha</span>
        <input name="p-senha" type="password" autocomplete="new-password" />
      </label>
      <label class="campo">
        <span>Confirme a senha</span>
        <input name="p-senha2" type="password" autocomplete="new-password" />
      </label>
      <p class="primeiro-dica">Minimo de ${opcoes.minimoSenha} caracteres.</p>
      <button type="button" class="botao-principal" data-criar>Criar primeiro acesso</button>
      <p class="primeiro-erro" role="alert" hidden></p>
      <p class="primeiro-ok" role="status" hidden></p>
    </div>
  `;
  cartao.appendChild(bloco);

  const abrir = bloco.querySelector<HTMLButtonElement>('[data-abrir]')!;
  const form = bloco.querySelector<HTMLDivElement>('.primeiro-form')!;
  const criar = bloco.querySelector<HTMLButtonElement>('[data-criar]')!;
  const erro = bloco.querySelector<HTMLParagraphElement>('.primeiro-erro')!;
  const okMsg = bloco.querySelector<HTMLParagraphElement>('.primeiro-ok')!;
  const campo = (n: string) => bloco.querySelector<HTMLInputElement>(`[name=${n}]`)!;

  abrir.setAttribute('aria-expanded', 'false');
  abrir.addEventListener('click', () => {
    form.hidden = !form.hidden;
    abrir.setAttribute('aria-expanded', String(!form.hidden));
    abrir.hidden = !form.hidden ? true : false;
    if (!form.hidden) campo('p-nome').focus();
  });

  criar.addEventListener('click', () => {
    erro.hidden = true;
    okMsg.hidden = true;

    const nome = campo('p-nome').value.trim();
    const email = campo('p-email').value.trim();
    const senha = campo('p-senha').value;
    const senha2 = campo('p-senha2').value;

    /* Conferencias locais que evitam ida ao servidor. Elas NAO substituem as do
       servidor — apenas poupam uma viagem obvia. Quem decide continua sendo o
       backend, que refaz todas. */
    const local =
      !nome ? 'Informe o nome.'
      : !email ? 'Informe o e-mail.'
      : senha.length < opcoes.minimoSenha ? `A senha precisa de pelo menos ${opcoes.minimoSenha} caracteres.`
      : senha !== senha2 ? 'A senha e a confirmacao nao conferem.'
      : null;
    if (local) {
      erro.textContent = local;
      erro.hidden = false;
      return;
    }

    criar.disabled = true;
    criar.textContent = 'Criando…';
    void opcoes
      .aoCriar(nome, email, senha, senha2)
      .then(() => {
        form.hidden = true;
        abrir.hidden = true;
        okMsg.textContent = 'Conta criada. Entre acima com o e-mail e a senha que voce acabou de definir.';
        okMsg.hidden = false;
        /* A senha nao fica no DOM depois de usada. */
        campo('p-senha').value = '';
        campo('p-senha2').value = '';
      })
      .catch((e: Error & { corpo?: Record<string, unknown> }) => {
        erro.textContent = mensagemDeErro(e);
        erro.hidden = false;
      })
      .finally(() => {
        criar.disabled = false;
        criar.textContent = 'Criar primeiro acesso';
      });
  });
}

/* O servidor devolve um codigo; a tela traduz. Codigo desconhecido nao vira
   texto tecnico na cara do usuario, mas tambem nao vira mentira: vira o texto
   que o servidor mandou, se houver. */
function mensagemDeErro(e: Error & { corpo?: Record<string, unknown> }): string {
  const corpo = e.corpo ?? {};
  const codigo = String(corpo.error ?? e.message ?? '');
  const mapa: Record<string, string> = {
    bootstrap_closed: 'O primeiro acesso nao esta liberado. Fale com quem administra o servidor.',
    bootstrap_already_done: 'Esta organizacao ja tem um responsavel. Entre pela tela de login.',
    email_in_use: 'Este e-mail ja esta cadastrado.',
    password_mismatch: 'A senha e a confirmacao nao conferem.',
    password_policy: String(corpo.message ?? 'A senha nao atende a politica minima.'),
    unexpected_field: 'Pedido invalido.',
    organization_not_unique: 'Ha mais de uma organizacao; o primeiro acesso nao pode escolher.',
    organization_missing: 'Nao ha organizacao para vincular.',
    too_many_attempts: 'Muitas tentativas. Aguarde alguns minutos.',
  };
  return mapa[codigo] ?? String(corpo.message ?? 'Nao foi possivel criar o primeiro acesso.');
}
